import React from 'react';

import { Chip, useTheme } from '@mui/material';
import { DisplayFilters } from 'src/utils/filters/display-filters';
import { toggleTag } from 'src/utils/filters/selected-filters';
import NoAntibioticsIcon from '../Icons/NoAntibioticsIcon';

const NAE_PROTEIN_TAG_LOGICAL_ID = 'NAE_PROTEIN';
const CONVENTIONAL_PROTEIN_TAG_LOGICAL_ID = 'CONVENTIONAL';

interface Props {
  displayFilters: DisplayFilters;
  selectedFilters: DisplayFilters;
  setSelectedFilters: React.Dispatch<React.SetStateAction<DisplayFilters>>;
}

const SOURCING_STANDARD_TAG_CATEGORY_LOGICAL_ID = 'SOURCING_STANDARD';

const SourcingStandardFilter: React.FC<Props> = ({
  displayFilters,
  selectedFilters,
  setSelectedFilters,
}) => {
  const theme = useTheme();

  const sourcingStandardTagCategory = displayFilters.tagCategories?.find(
    (tagCategory) =>
      tagCategory.logicalId === SOURCING_STANDARD_TAG_CATEGORY_LOGICAL_ID,
  );

  if (!sourcingStandardTagCategory) {
    // This might occur on an initial render
    return null;
  }

  /**
   * Do not show this filter if the catalog only contains meals with a single sourcing standard.
   * Showing it could cause user confusion since some tenants' dietary rules never allow for anything
   * other than NAE.
   */
  if ((sourcingStandardTagCategory?.tags?.length || 0) === 1) {
    return null;
  }

  const noAntibioticsEverTag = sourcingStandardTagCategory?.tags.find(
    (tag) => tag.logicalId === NAE_PROTEIN_TAG_LOGICAL_ID,
  );

  if (!noAntibioticsEverTag) {
    // Do not show the filter if there are no meals on the menu that it would apply to.
    return null;
  }

  const conventionalProteinTag = sourcingStandardTagCategory?.tags.find(
    (tag) => tag.logicalId === CONVENTIONAL_PROTEIN_TAG_LOGICAL_ID,
  );

  if (!conventionalProteinTag) {
    // Do not show the filter if there are no non-NAE (conventional) protein meals on the menu. The length check above
    // will not catch this because `ORGANIC` sourcing standard tags may also be present.
    return null;
  }

  const isSelected = selectedFilters.tagCategories
    ?.find(
      (tagCategory) =>
        tagCategory.logicalId === SOURCING_STANDARD_TAG_CATEGORY_LOGICAL_ID,
    )
    ?.tags.find((tag) => tag.logicalId === NAE_PROTEIN_TAG_LOGICAL_ID);

  const iconFill = isSelected
    ? theme.palette.primary.contrastText
    : theme.palette.text.primary;

  return (
    <Chip
      label={'No antibiotics'}
      icon={<NoAntibioticsIcon fill={iconFill} height={14} width={14} />}
      style={{
        margin: '0 0.35rem 0 0',
        borderRadius: 4,
        fontSize: '0.875rem',
        padding: '6px 8px',
        columnGap: 6,
      }}
      sx={{
        '& .MuiChip-label': {
          padding: '2px',
        },
      }}
      size={'medium'}
      color={isSelected ? 'primary' : 'default'}
      onClick={() => {
        setSelectedFilters((prevState) =>
          toggleTag({
            displayFilters,
            currentFilters: prevState,
            tagId: noAntibioticsEverTag.id,
          }),
        );
      }}
    />
  );
};

export default SourcingStandardFilter;
